// src/pages/finance-portal/pay-stubs/PayStubs.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Alert, Skeleton,
    InputAdornment, Autocomplete, CircularProgress,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PayStubs() {
    const navigate = useNavigate();
    const [stubs, setStubs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Employee filter
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeLoading, setEmployeeLoading] = useState(false);
    const employeeTimer = useRef(null);

    // Date range
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchStubs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (selectedEmployee) params.employee_id = selectedEmployee.id;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const res = await axiosInstance.get('/api/finance/payroll/pay-stubs', { params });
            if (res.data.success) {
                setStubs(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load pay stubs');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, selectedEmployee, fromDate, toDate]);

    useEffect(() => { fetchStubs(); }, [fetchStubs]);

    // Employee search with debounce
    useEffect(() => {
        if (employeeTimer.current) clearTimeout(employeeTimer.current);
        const params = { per_page: 50, status: 'ACTIVE' };
        if (employeeSearch && employeeSearch.length >= 2) params.search = employeeSearch;
        setEmployeeLoading(true);
        employeeTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/payroll/employees', { params });
                if (res.data.success) {
                    setEmployeeOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search employees', err);
            } finally {
                setEmployeeLoading(false);
            }
        }, employeeSearch ? 300 : 0);
        return () => { if (employeeTimer.current) clearTimeout(employeeTimer.current); };
    }, [employeeSearch]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Pay Stubs</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>View employee pay stub history</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Autocomplete
                        size="small"
                        options={employeeOptions}
                        getOptionLabel={opt => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() + (opt.employee_number ? ` (${opt.employee_number})` : '') : ''}
                        value={selectedEmployee}
                        onChange={(_, val) => { setSelectedEmployee(val); setPage(0); }}
                        onInputChange={(_, val) => setEmployeeSearch(val)}
                        loading={employeeLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Filter by employee"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' },
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {employeeLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        sx={{ minWidth: { xs: '100%', sm: 280 } }}
                        noOptionsText="Type to search employees"
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="From Date"
                        value={fromDate}
                        onChange={e => { setFromDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: { xs: '100%', sm: 150 } }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To Date"
                        value={toDate}
                        onChange={e => { setToDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: { xs: '100%', sm: 150 } }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Stub #', 'Employee', 'Period', 'Pay Date', 'Gross Pay', 'Taxes', 'Deductions', 'Net Pay'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : stubs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <ReceiptIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No pay stubs found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : stubs.map(stub => {
                                    const empName = stub.employee_name || [stub.first_name, stub.last_name].filter(Boolean).join(' ') || '—';
                                    const totalTaxes = (Number(stub.federal_tax) || 0) +
                                        (Number(stub.social_security_tax || stub.ss_tax) || 0) +
                                        (Number(stub.medicare_tax) || 0) +
                                        (Number(stub.state_tax) || 0);
                                    return (
                                        <TableRow
                                            key={stub.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/pay-stubs/${stub.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {stub.stub_number || stub.id || '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{empName}</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>{stub.employee_number || ''}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(stub.period_start)} - {formatDate(stub.period_end)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(stub.pay_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(stub.gross_pay)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#dc2626' }}>
                                                {formatCurrency(totalTaxes)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatCurrency(stub.total_deductions || stub.deductions)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: ACCENT }}>
                                                {formatCurrency(stub.net_pay)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>
        </Box>
    );
}
